import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API from '../context/api';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const { data } = await API.post(
        `/api/users/reset-password/${token}`,
        { password }
      );

      setSuccess(data.message || 'Password updated successfully');

      setTimeout(() => navigate('/login'), 2000);

    } catch (err) {
      setError(err.response?.data?.message || 'Reset link expired or invalid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>

      {/* BACKGROUND BLOBS */}
      <div style={styles.blob1} />
      <div style={styles.blob2} />
      <div style={styles.blob3} />

      {/* LEFT PANEL (hidden on mobile) */}
      {!isMobile && (
        <div style={styles.leftPanel}>
          <div style={styles.overlay} />
          <div style={styles.hero}>
            <h1 style={styles.heroTitle}>Reset<br />Password</h1>
            <p style={styles.heroText}>
              Secure your account with a new strong password.
            </p>
          </div>
        </div>
      )}

      {/* RIGHT PANEL */}
      <div style={{
        ...styles.rightPanel,
        width: isMobile ? '100%' : '50%'
      }}>

        <div style={{
          ...styles.card,
          width: isMobile ? '92%' : '100%'
        }}>

          <h1 style={styles.title}>Set New Password</h1>
          <p style={styles.subtitle}>
            Choose a strong password you haven’t used before
          </p>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>

            <input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
            />

            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>

          </form>

          <div style={styles.bottom}>
            <Link to="/login" style={styles.link}>
              Back to Login
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ResetPassword;

/* ================= INLINE STYLES ================= */

const styles = {

  page: {
    minHeight: '100vh',
    display: 'flex',
    background: '#020617',
    fontFamily: 'Inter, sans-serif',
    position: 'relative',
    overflow: 'hidden'
  },

  /* BLOBS */
  blob1: {
    position: 'absolute',
    width: 650,
    height: 650,
    top: -250,
    left: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #6366f1 0%, transparent 60%)',
    filter: 'blur(120px)',
    opacity: 0.45
  },

  blob2: {
    position: 'absolute',
    width: 650,
    height: 650,
    bottom: -280,
    right: -250,
    borderRadius: '50%',
    background: 'radial-gradient(circle, #0ea5e9 0%, transparent 60%)',
    filter: 'blur(140px)',
    opacity: 0.35
  },

  blob3: {
    position: 'absolute',
    width: 500,
    height: 500,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #8b5cf6 0%, transparent 65%)',
    filter: 'blur(160px)',
    opacity: 0.18
  },

  /* LEFT PANEL */
  leftPanel: {
    flex: 1,
    position: 'relative',
    background:
      'url(https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1800&q=80)',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  },

  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(2,6,23,.95), rgba(15,23,42,.7))'
  },

  hero: {
    position: 'relative',
    zIndex: 2,
    padding: 80,
    color: '#fff',
    maxWidth: 520
  },

  heroTitle: {
    fontSize: 60,
    fontWeight: 900,
    lineHeight: 1.1
  },

  heroText: {
    marginTop: 16,
    color: '#cbd5e1'
  },

  /* RIGHT PANEL */
  rightPanel: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    position: 'relative',
    zIndex: 10
  },

  /* CARD */
  card: {
    maxWidth: 460,
    padding: 42,
    borderRadius: 32,
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(255,255,255,.12)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
    boxShadow: '0 40px 120px rgba(0,0,0,.6)',
    color: '#fff'
  },

  title: {
    fontSize: 32,
    fontWeight: 900
  },

  subtitle: {
    color: '#94a3b8',
    marginTop: 6,
    marginBottom: 20
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },

  input: {
    height: 56,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,.12)',
    background: 'rgba(255,255,255,.05)',
    color: '#fff',
    padding: '0 16px',
    fontSize: 15,
    outline: 'none'
  },

  button: {
    height: 58,
    borderRadius: 18,
    border: 'none',
    fontWeight: 800,
    color: '#fff',
    background: 'linear-gradient(135deg,#6366f1,#3b82f6,#06b6d4)'
  },

  bottom: {
    marginTop: 18,
    textAlign: 'center'
  },

  link: {
    color: '#60a5fa',
    fontWeight: 700,
    textDecoration: 'none'
  },

  error: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 12,
    background: 'rgba(239,68,68,.15)',
    border: '1px solid rgba(239,68,68,.3)',
    color: '#fecaca'
  },

  success: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 12,
    background: 'rgba(34,197,94,.15)',
    border: '1px solid rgba(34,197,94,.3)',
    color: '#bbf7d0'
  }
};